import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Grid } from '@material-ui/core';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import FormikSelectField from '../../../../../FormikFields/FormikSelectField';
import { CLAIM_REPORT_TYPES } from '../../../../constants';
import CptsModal from './CptModal/CptsModal';
import HelperText from '../../../../../Shared/HelperText';
import RoundedButton from '../../../../../Shared/RoundedButton';
import { setAccountBackendErrors } from '../../../../../../redux_store';

const useStyles = makeStyles((theme) => ({
  fullWidth: {
    width: '100%',
    marginTop: 0,
  },
  statesSelect: {
    marginTop: theme.spacing(1),
  },
  buttonContainer: {
    padding: '0 0 0 12px !important',
  },
  contentContainer: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingBottom: 12,
  },
  addNewButton: {
    boxShadow: '1px 4px 15px -5px rgba(0, 0, 0, 0.97)',
  },
  addNewButtonWithError: {
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      opacity: 0.8,
    },
  },
  activitiesError: {
    color: theme.palette.error.main,
    height: 20,
    marginLeft: 14,
    marginTop: 8,
  },
  services: {
    transition: 'display 0.6s linear',
    display: 'none',
  },
  showServices: {
    opacity: 1,
    transition: 'opacity 0.6s linear',
    display: 'contents',
  },
}));

const ClaimReportTypeSection = ({
                                  values,
                                  disabled = false,
                                }) => {
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const [openCptsModal, toggleCptsModal] = useState(false);
  const dispatch = useDispatch();
  const { backendErrors } = useSelector((state) => state.accountEditing);
  const setBackendErrors = (newErrors) => {
    dispatch(setAccountBackendErrors(newErrors));
  }

  const errors = useMemo(
    () => (backendErrors
      && ((Array.isArray(backendErrors?.ispConfiguration?.services)
          && backendErrors.ispConfiguration.services.filter(services => services && Object.keys(services).length).length)
        || Object.keys(backendErrors).some(k => k.startsWith('ispConfiguration.services'))
      )
    ),
    [backendErrors],
  );

  const errorsForInput = useMemo(
    () => {
      if (typeof backendErrors?.ispConfiguration === 'string') {
        return backendErrors?.ispConfiguration;
      }
      if (backendErrors?.ispConfiguration && typeof backendErrors?.ispConfiguration?.claimReportType === 'string') {
        return backendErrors?.ispConfiguration?.claimReportType;
      }
    },
    [backendErrors],
  );

  const errorsWithoutServices = useMemo(
    () => {
      if (backendErrors?.ispConfiguration?.services
        && Array.isArray(backendErrors.ispConfiguration.services)) {
        const _error = { ...backendErrors };
        delete backendErrors?.ispConfiguration?.services;
        return !!Object.keys(_error?.ispConfiguration || {}).length;
      }
      return !!Object.keys((backendErrors?.ispConfiguration) || {}).length || backendErrors && typeof backendErrors['ispConfiguration.Services'] === 'string';
    },

    [backendErrors],
  );

  return (
    <div style={{ width: '100%' }}>
      <Grid container spacing={3} className={classes.contentContainer}>
        <Grid item xs={4}>
          <FormikSelectField
            name={'ispConfiguration.claimReportType'}
            label={forms.claimReportType}
            type="text"
            hideErrorAreaIfNoErrors
            menuItems={CLAIM_REPORT_TYPES}
            className={cx(classes.statesSelect, classes.fullWidth)}
            backendError={errorsForInput}
            disabled={disabled}
          />
        </Grid>
        <div
          className={cx(classes.services, {
            [classes.showServices]: values.ispConfiguration && values.ispConfiguration?.claimReportType,
          })}
        >
          <Grid item xs={4} className={classes.buttonContainer}>
            <RoundedButton
              variant="contained"
              color={errors || errorsWithoutServices ? 'error' : 'primary'}
              size="small"
              disabled={disabled}
              className={cx(classes.addNewButton, {
                [classes.addNewButtonWithError]: errors || errorsWithoutServices,
              })}
              onClick={() => {
                if (backendErrors?.ispConfiguration && typeof backendErrors.ispConfiguration === 'string') {
                  const newErrors = { ...errorsWithoutServices };
                  delete newErrors?.ispConfiguration;
                  setBackendErrors(newErrors);
                }
                if (backendErrors['ispConfiguration.Services']) {
                  const newErrors = { ...errorsWithoutServices };
                  delete newErrors['ispConfiguration.Services'];
                  setBackendErrors(newErrors);
                }
                toggleCptsModal(true);
              }}
            >
              {forms.manageCpts}
            </RoundedButton>
          </Grid>
          {
            errors ? <HelperText className={cx(classes.activitiesError)}>
              {'Please check CPTs table. There are some errors.'}
            </HelperText> : <></>
          }
          {
            backendErrors && typeof backendErrors['ispConfiguration.Services'] === 'string'
              ? <HelperText className={cx(classes.activitiesError)}>
                {backendErrors['ispConfiguration.Services']}
              </HelperText>
              : <></>
          }
        </div>
      </Grid>

      <CptsModal
        values={values}
        toggleCptsModal={toggleCptsModal}
        openCptsModal={openCptsModal}
      />
    </div>
  );
};

ClaimReportTypeSection.propTypes = {
  values: PropTypes.shape({}),
  backendErrors: PropTypes.shape({}),
  disabled: PropTypes.bool,
};

ClaimReportTypeSection.defaultProps = {
  values: {},
  backendErrors: {},
};

export default ClaimReportTypeSection;
